/**
 * Cross-form reference helpers — client mirror of the backend
 * `form-engine/cross-form-refs.ts`. Keep the two in sync.
 *
 * A cross-form ref token is `{<formHandle>.<fieldId>}`: the segment before the
 * first dot is the referenced form's permanent handle, the rest is a field id
 * on that form. Same-form ids (UUIDs, table `${rowId}__${colId}` cell keys,
 * Excel-import Field IDs — all dot-free) never collide with this shape, so
 * "parses as handle.fieldId" is the discriminator.
 *
 * Resolution is a single mechanism shared by calculated formulas AND
 * visibility conditions: build a CrossFormContext from the patient's journey,
 * merge its namespaced values under the live values map, and evaluate as
 * usual. Sources carry a `kind` so a future shared-values pool (approach C)
 * can plug in (e.g. a reserved `shared.` prefix) without changing any stored
 * reference or this API.
 */
import type { FormDefinition } from '@/types/cgt'
import {
  allFields,
  visibilitySourceIds,
  notificationSourceIds,
  notificationRules,
} from '@/lib/formSections'
import { extractFormulaRefs } from '@/lib/formula'
import { calculatedCellFormulas } from '@/lib/tableForm'

/** Valid form handle: lowercase alphanumeric runs separated by single hyphens. */
export const FORM_HANDLE_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/

export interface CrossFormRef {
  handle: string
  fieldId: string
}

/** Parse a ref token into handle + fieldId, or null when it is a same-form ref. */
export function parseCrossFormRef(ref: string): CrossFormRef | null {
  const dot = ref.indexOf('.')
  if (dot <= 0 || dot === ref.length - 1) return null
  const handle = ref.slice(0, dot)
  if (!FORM_HANDLE_RE.test(handle)) return null
  return { handle, fieldId: ref.slice(dot + 1) }
}

export function isCrossFormRef(ref: string): boolean {
  return parseCrossFormRef(ref) !== null
}

/** The namespaced values-map key a cross-form ref resolves under. */
export function crossFormKey(handle: string, fieldId: string): string {
  return `${handle}.${fieldId}`
}

/**
 * Rewrite the quoted import spelling `{"formhandle".fieldId}` to the canonical
 * `{formhandle.fieldId}`. Excel authors may quote the handle for readability;
 * stored formulas always use the canonical dot form.
 */
export function normalizeCrossFormRefSyntax(formula: string): string {
  return formula.replace(
    /\{\s*"([a-z0-9]+(?:-[a-z0-9]+)*)"\s*\.\s*([^}\s][^}]*?)\s*\}/g,
    '{$1.$2}',
  )
}

/**
 * Normalize a form name into a handle base: lowercase, accents stripped,
 * non-alphanumerics collapsed to single hyphens. May return '' for names with
 * no usable characters — callers substitute a fallback base.
 */
export function normalizeFormHandle(name: string): string {
  return name
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

/** `len` random base36 chars. `slice(2)` drops the "0." float prefix so the
 *  suffix draws from the full alphabet instead of always starting with '0'. */
function randomIdSuffix(len: number): string {
  return Math.random().toString(36).slice(2).replace(/[^a-z0-9]/g, '').padEnd(len, '0').slice(0, len)
}

/**
 * Suggest a full handle for a new form: normalized name + 3 random chars.
 * Preview only — the backend re-checks uniqueness and generates its own value
 * when the creation payload omits the handle.
 */
export function suggestFormHandle(name: string): string {
  const base = normalizeFormHandle(name) || 'form'
  return `${base}-${randomIdSuffix(3)}`
}

/**
 * Readable field id: normalized label + 3 random chars, mirroring
 * {@link suggestFormHandle} — e.g. "Patient Weight (kg)" → `patient-weight-kg-x7q`.
 * Minted once at field creation; renaming the label later never changes the id
 * (submissions, formulas and audit entries all key on it), exactly like a form's
 * handle survives a rename.
 *
 * Unlike handles there is no backend uniqueness re-check, so callers that know
 * the form pass `usedIds` (every field id already on the form): the suffix is
 * regenerated on collision and the returned id is added to the set, which lets
 * bulk callers (Excel import, insert-category) share one set across a loop.
 */
export function suggestFieldId(label: string, usedIds?: Set<string>): string {
  // Cap sentence-length labels so formula tokens stay manageable.
  const base = (normalizeFormHandle(label) || 'field').slice(0, 40).replace(/-+$/, '')
  let id = `${base}-${randomIdSuffix(3)}`
  // Collisions only occur between same-base fields; retry, then widen for safety.
  for (let attempt = 1; usedIds?.has(id); attempt++) {
    id = `${base}-${randomIdSuffix(attempt < 8 ? 3 : 8)}`
  }
  usedIds?.add(id)
  return id
}

/**
 * Rewrite every same-form `{token}` in a formula through `tokenMap`, leaving
 * cross-form refs (`{handle.fieldId}`) and unmapped tokens exactly as they are.
 *
 * The one rewriter for every path that re-mints field ids — copying a form
 * (`formCopy.ts`) and importing one from a spreadsheet (`formExcel.ts`). It
 * exists as a shared function because both of those must run it as a SECOND
 * pass: `suggestFieldId` appends a random suffix, so a placed field's id is
 * unknowable until it is minted, and a formula can reference a sibling that
 * has not been minted yet during a single walk. Mint the whole cohort, build
 * the map, then rewrite.
 *
 * `tokenMap` is keyed on whatever namespace the formula was written in — a
 * source form's field ids when copying, a library field's `refName` when
 * importing. Unmapped tokens are deliberately left alone rather than dropped:
 * the caller decides whether that is an error or a fall-through to the
 * label-similarity guess.
 */
export function remapFormulaTokens(
  formula: string | undefined,
  tokenMap: ReadonlyMap<string, string>,
): string | undefined {
  if (!formula) return formula
  return formula.replace(/\{([^}]+)\}/g, (whole, id: string) => {
    if (isCrossFormRef(id)) return whole
    const mapped = tokenMap.get(id)
    return mapped ? `{${mapped}}` : whole
  })
}

// ─── Resolution context ──────────────────────────────────────────────────────

/**
 * One referenceable upstream form within the patient's journey. `values` is
 * null while the form has no submission yet ("waiting"). `kind` discriminates
 * future source types (approach C's shared pool) without touching stored refs.
 */
export interface CrossFormSource {
  kind: 'form'
  handle: string
  formName: string
  /** field id → label, from the step's form definition. */
  fieldLabels: Record<string, string>
  values: Record<string, unknown> | null
  /**
   * The form was skipped in this run (z8nrz7fn5r): it supplies no values, and
   * rules and gates that read it are ignored (see lib/skippedSources).
   */
  skipped?: boolean
}

export type CrossRefState =
  | { status: 'ok' }
  /** The upstream form exists in the journey but has not been filled yet. */
  | { status: 'waiting'; formName: string }
  /** The upstream form was skipped: refs to it are ignored, not read as empty. */
  | { status: 'skipped'; formName: string }
  /** No form with this handle exists in the patient's journey. */
  | { status: 'missing-form'; handle: string }
  /** The upstream form exists but no longer has this field. */
  | { status: 'missing-field'; formName: string; fieldId: string }

export interface CrossFormResolution {
  /** Namespaced values ("handle.fieldId" → value) to merge UNDER live values. */
  values: Record<string, unknown>
  /** Namespaced labels ("handle.fieldId" → "Label (Form Name)") for summaries. */
  labelById: Record<string, string>
  stateByRef: Record<string, CrossRefState>
  /** Handles of the run's skipped forms that this definition reads. */
  skippedHandles: string[]
}

/**
 * Every cross-form ref used by a definition's calculated formulas (fields and
 * calculated table cells), visibility rules (field + section level), and
 * field notification rules, deduplicated in first-seen order.
 */
export function collectCrossFormRefs(definition: FormDefinition): string[] {
  const refs: string[] = []
  const add = (token: string) => {
    if (isCrossFormRef(token) && !refs.includes(token)) refs.push(token)
  }
  for (const field of allFields(definition)) {
    if (field.formula) extractFormulaRefs(field.formula).forEach(add)
    // Missing these left the host page with no refs to resolve, so a cell
    // reading an earlier form rendered "—" forever.
    for (const formula of calculatedCellFormulas(field.tableConfig)) extractFormulaRefs(formula).forEach(add)
    visibilitySourceIds(field.visibility).forEach(add)
    // Every rule on the field, not just the first: each carries its own
    // condition tree and so its own cross-form sources.
    for (const rule of notificationRules(field)) notificationSourceIds(rule).forEach(add)
  }
  // Section-level visibility rules live on the sections themselves.
  for (const section of definition.sections ?? []) {
    visibilitySourceIds(section.visibility).forEach(add)
  }
  // Gates read other forms too (z8nrz7fn5r). Left out, a gate on another
  // form's field read it as empty and failed closed, and a skipped source could
  // not be recognised to have its gate ignored.
  for (const field of allFields(definition)) {
    for (const gate of [field.unlockGate, field.signatureGate]) {
      if (gate?.formula) extractFormulaRefs(gate.formula).forEach(add)
    }
  }
  if (definition.completionGate?.formula) extractFormulaRefs(definition.completionGate.formula).forEach(add)
  return refs
}

/**
 * Resolve refs against the journey's sources. Unresolvable refs simply have no
 * entry in `values` (the evaluators treat missing keys as empty → formulas
 * render "—", conditions evaluate as not met) and carry a `stateByRef` entry
 * that drives the waiting/warning UI.
 */
export function resolveCrossRefs(
  refs: string[],
  sources: CrossFormSource[],
): CrossFormResolution {
  const byHandle = new Map<string, CrossFormSource>()
  for (const s of sources) byHandle.set(s.handle, s)

  const values: Record<string, unknown> = {}
  const labelById: Record<string, string> = {}
  const stateByRef: Record<string, CrossRefState> = {}
  const skippedHandles = new Set<string>()

  for (const ref of refs) {
    const parsed = parseCrossFormRef(ref)
    if (!parsed) continue
    const source = byHandle.get(parsed.handle)
    if (!source) {
      stateByRef[ref] = { status: 'missing-form', handle: parsed.handle }
      continue
    }
    const label = source.fieldLabels[parsed.fieldId]
    if (label === undefined) {
      stateByRef[ref] = {
        status: 'missing-field',
        formName: source.formName,
        fieldId: parsed.fieldId,
      }
      continue
    }
    labelById[ref] = `${label} (${source.formName})`
    // Skipped on purpose: nothing it holds is read as an answer.
    if (source.skipped) {
      stateByRef[ref] = { status: 'skipped', formName: source.formName }
      skippedHandles.add(parsed.handle)
      continue
    }
    if (source.values === null) {
      stateByRef[ref] = { status: 'waiting', formName: source.formName }
      continue
    }
    stateByRef[ref] = { status: 'ok' }
    if (source.values[parsed.fieldId] !== undefined) {
      values[ref] = source.values[parsed.fieldId]
    }
  }
  return { values, labelById, stateByRef, skippedHandles: [...skippedHandles] }
}

/**
 * Build the sources list from the data the procedure page / renderer host already
 * holds: journey steps (with formHandle) + each step's definition and
 * submission. Steps without a resolvable definition are skipped — their refs
 * surface as 'missing-form'.
 */
export function buildCrossFormSources(
  steps: Array<{
    formHandle?: string
    formName: string
    definition?: Pick<FormDefinition, 'sections'> | null
    values?: Record<string, unknown> | null
    /** The step's form was skipped in this run (z8nrz7fn5r). */
    skipped?: boolean
  }>,
): CrossFormSource[] {
  const sources: CrossFormSource[] = []
  for (const step of steps) {
    if (!step.formHandle || !step.definition) continue
    const fieldLabels: Record<string, string> = {}
    for (const f of allFields(step.definition as FormDefinition)) {
      fieldLabels[f.id] = f.label
    }
    sources.push({
      kind: 'form',
      handle: step.formHandle,
      formName: step.formName,
      fieldLabels,
      values: step.values ?? null,
      ...(step.skipped ? { skipped: true } : {}),
    })
  }
  return sources
}
